import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadIcon from "@mui/icons-material/Download";
import { SectionCard } from "./SectionCard";
import type { ClientDetails, CreateClientResponse } from "../api/clientManagement";
import type { CreateClientFormState } from "../types";

type ClientServiceField =
  | "learning_locker_enabled"
  | "occupational_health_enabled"
  | "wearables_enabled"
  | "akea_life_enabled";

interface ClientTabProps {
  effectiveClientId: string | null;
  isLoading: boolean;
  error: unknown;
  client: ClientDetails | null;
  isCentralAdmin: boolean;
  onToggleAnonymisation: (value: boolean) => void;
  onToggleUserManagement: (value: boolean) => void;
  onToggleClientService: (field: ClientServiceField, value: boolean) => void;
  anonymisationPending: boolean;
  createClientForm: CreateClientFormState;
  onCreateClientChange: (field: keyof CreateClientFormState, value: string | boolean) => void;
  onCreateClient: () => void;
  createClientPending: boolean;
  createClientResult: CreateClientResponse | null;
  onDismissCreateClientResult: () => void;
  onUpdateClientName: (name: string) => void;
  updateClientNamePending: boolean;
}

// ---------------------------------------------------------------------------
// Settings toggle row — clean label + description + switch
// ---------------------------------------------------------------------------

const SettingsRow: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  indent?: boolean;
}> = ({ label, description, checked, disabled, onChange, indent }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      pl: indent ? 2 : 0,
      ...(indent && { borderLeft: "2px solid", borderColor: "divider" }),
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      size="small"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  </Box>
);

// ---------------------------------------------------------------------------
// Service row (same as SettingsRow but with a short label suffix chip)
// ---------------------------------------------------------------------------

const ServiceRow: React.FC<{
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: checked ? "success.main" : "action.disabled",
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
    </Stack>
    <Switch
      size="small"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ClientTab: React.FC<ClientTabProps> = ({
  effectiveClientId,
  isLoading,
  error,
  client,
  isCentralAdmin,
  onToggleAnonymisation,
  onToggleUserManagement,
  onToggleClientService,
  anonymisationPending,
  createClientForm,
  onCreateClientChange,
  onCreateClient,
  createClientPending,
  createClientResult,
  onDismissCreateClientResult,
  onUpdateClientName,
  updateClientNamePending,
}) => {
  const hasResult = !!createClientResult;
  const hasMissingSeeds = createClientResult
    ? createClientResult.data.created_departments === 0 ||
      createClientResult.data.created_job_roles === 0
    : false;
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [clientName, setClientName] = useState("");

  useEffect(() => {
    if (hasResult) setCreateModalOpen(false);
  }, [hasResult]);

  useEffect(() => {
    if (client?.name) {
      setClientName(client.name);
      setEditingName(false);
    }
  }, [client?.name]);

  const downloadQrCode = async () => {
    if (!client?.qrCodeUrl) return;
    const safeName = (client?.name || "client")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const fileName = `${safeName || "client"}-qr.png`;
    try {
      const response = await fetch(client.qrCodeUrl);
      if (!response.ok) throw new Error("Failed to fetch QR code");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(client.qrCodeUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSaveClientName = () => {
    if (clientName.trim() && clientName !== client?.name) {
      onUpdateClientName(clientName.trim());
    }
    setEditingName(false);
  };

  const isActive = client?.is_active !== false && client?.isActive !== false;
  const anonymisationOn = Boolean(client?.enable_anonymisation ?? client?.enableAnonymisation);
  const userManagementOn = Boolean(client?.enable_user_management ?? client?.enableUserManagement);

  return (
    <Stack spacing={2.5}>
      {/* Header action */}
      {isCentralAdmin && (
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" onClick={() => setCreateModalOpen(true)}>
            Create client
          </Button>
        </Box>
      )}

      {Boolean(error) && (
        <Alert severity="error">Failed to load client details. Please try again.</Alert>
      )}

      {!effectiveClientId ? (
        <Alert severity="info">Select a client to view details.</Alert>
      ) : isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading client details…</Typography>
        </Stack>
      ) : client ? (
        <>
          {/* Identity card */}
          <SectionCard>
            <Stack spacing={2}>
              {/* Name row */}
              <Box display="flex" alignItems="center" gap={1.5}>
                {editingName && isCentralAdmin ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      disabled={updateClientNamePending}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveClientName();
                        if (e.key === "Escape") {
                          setClientName(client?.name || "");
                          setEditingName(false);
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!clientName.trim() || clientName === client?.name || updateClientNamePending}
                        onClick={handleSaveClientName}
                      >
                        {updateClientNamePending ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={() => { setClientName(client?.name || ""); setEditingName(false); }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <>
                    <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                      {client?.name ?? "Client"}
                    </Typography>
                    {isCentralAdmin && (
                      <Tooltip title="Rename client">
                        <IconButton size="small" onClick={() => setEditingName(true)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  size="small"
                  color={isActive ? "success" : "default"}
                  variant="outlined"
                />
              </Box>

              {/* QR code row */}
              <Divider />
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75} mb={1.5}>
                  <QrCode2Icon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography variant="body2" fontWeight={500} color="text.secondary">
                    Client QR Code
                  </Typography>
                </Stack>
                {client?.qrCodeUrl ? (
                  <Stack direction="row" spacing={2} alignItems="flex-end">
                    <Box
                      component="img"
                      src={client.qrCodeUrl}
                      alt="Client QR code"
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <Stack spacing={1} alignItems="flex-start">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={downloadQrCode}
                      >
                        Download
                      </Button>
                      {client?.qrCodeValue && (
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, wordBreak: "break-all" }}>
                          {client.qrCodeValue}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    QR code not yet generated. Save the client to trigger generation.
                  </Typography>
                )}
              </Box>
            </Stack>
          </SectionCard>

          {/* Privacy */}
          {isCentralAdmin && (
            <SectionCard title="Privacy" description="Control how participant data is surfaced.">
              <Stack spacing={0} divider={<Divider />}>
                <Box sx={{ py: 1.5 }}>
                  <SettingsRow
                    label="Enable anonymisation"
                    description="Filters and charts hide segments with fewer than two users."
                    checked={anonymisationOn}
                    disabled={anonymisationPending}
                    onChange={onToggleAnonymisation}
                  />
                </Box>

                {anonymisationOn ? (
                  <Box sx={{ py: 1.5 }}>
                    <Stack spacing={1.5}>
                      <SettingsRow
                        label="Enable user management access"
                        description="Allows client admins to access the Users section. Participant names will be visible."
                        checked={userManagementOn}
                        disabled={anonymisationPending}
                        onChange={onToggleUserManagement}
                        indent
                      />
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.5, ml: 2 }}>
                        Enabling this exposes participant names to client admins while anonymisation is active. Only enable if direct user oversight is required.
                      </Alert>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ py: 1.5, pl: 2, borderLeft: "2px solid", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary">
                      User management is always accessible when anonymisation is off.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </SectionCard>
          )}

          {/* Services */}
          {isCentralAdmin && (
            <SectionCard title="Enabled services" description="Toggle which products are active for this client.">
              <Stack spacing={0} divider={<Divider />}>
                {(
                  [
                    { field: "learning_locker_enabled" as ClientServiceField, label: "Learning Locker", value: Boolean(client.learning_locker_enabled ?? client.learningLockerEnabled) },
                    { field: "occupational_health_enabled" as ClientServiceField, label: "Occupational Health", value: Boolean(client.occupational_health_enabled ?? client.occupationalHealthEnabled) },
                    { field: "wearables_enabled" as ClientServiceField, label: "Wearables", value: Boolean(client.wearables_enabled ?? client.wearablesEnabled) },
                    { field: "akea_life_enabled" as ClientServiceField, label: "Akea Life", value: Boolean(client.akea_life_enabled ?? client.akeaLifeEnabled) },
                  ] as const
                ).map(({ field, label, value }) => (
                  <Box key={field} sx={{ py: 1.5 }}>
                    <ServiceRow
                      label={label}
                      checked={value}
                      disabled={anonymisationPending}
                      onChange={(checked) => onToggleClientService(field, checked)}
                    />
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          )}
        </>
      ) : null}

      <CreateClientToast
        open={!!createClientResult}
        result={createClientResult}
        onClose={onDismissCreateClientResult}
      />

      {/* Create client dialog */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Client name"
              value={createClientForm.name}
              onChange={(e) => onCreateClientChange("name", e.target.value)}
              autoFocus
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.is_active}
                  onChange={(e) => onCreateClientChange("is_active", e.target.checked)}
                />
              }
              label="Active"
            />
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Enabled services
            </Typography>
            {(
              [
                { key: "learning_locker_enabled", label: "Learning Locker" },
                { key: "occupational_health_enabled", label: "Occupational Health" },
                { key: "wearables_enabled", label: "Wearables" },
                { key: "akea_life_enabled", label: "Akea Life" },
              ] as const
            ).map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={Boolean(createClientForm[key])}
                    onChange={(e) => onCreateClientChange(key, e.target.checked)}
                  />
                }
                label={label}
              />
            ))}
            {hasResult && (
              <Alert severity={hasMissingSeeds ? "warning" : "success"}>
                Created {createClientResult?.data.created_departments} departments and{" "}
                {createClientResult?.data.created_job_roles} job roles.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createClientForm.name.trim() || createClientPending}
            onClick={onCreateClient}
          >
            {createClientPending ? <CircularProgress size={18} /> : "Create client"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

const CreateClientToast: React.FC<{
  open: boolean;
  result: CreateClientResponse | null;
  onClose: () => void;
}> = ({ open, result, onClose }) => {
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => setIsOpen(open), [open]);
  if (!result) return null;
  const hasMissingSeeds =
    result.data.created_departments === 0 || result.data.created_job_roles === 0;
  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={6000}
      onClose={() => { setIsOpen(false); onClose(); }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        onClose={() => { setIsOpen(false); onClose(); }}
        severity={hasMissingSeeds ? "warning" : "success"}
        variant="filled"
      >
        Client created with {result.data.created_departments} departments and{" "}
        {result.data.created_job_roles} job roles.
      </Alert>
    </Snackbar>
  );
};
